import React, { useState, useCallback, useEffect } from 'react';
import { Box, Typography, Divider, FormControl, TextField, FormHelperText, Button } from '@material-ui/core';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Link } from 'react-router-dom';
import { Autocomplete } from '@material-ui/lab';
import useAsync from 'react-use/esm/useAsync';
import { useStyles } from './styles.esm.js';
import { SkeletonComponent } from './skeletonComponent.esm.js';
export { ResourcePickerSchema } from './schema.esm.js';

const ResourcePicker = (props) => {
  const { uiSchema, schema, onChange, formData, required, rawErrors, idSchema } = props;
  const [catalogFilter, type] = resolveCatalogFilter(uiSchema);
  const catalogApi = useApi(catalogApiRef);
  const [entities, setEntities] = useState([]);
  const [entitySelected, setEntitySelected] = useState(null);
  const { autocompleteWrapper, descriptionContent, boxInfo } = useStyles();
  const { loading } = useAsync(async () => {
    const { items } = await catalogApi.getEntities(
      catalogFilter ? { filter: catalogFilter } : { filter: "" }
    );
    if (type) {
      const entityData = items.filter(
        (item) => item.spec && item.spec.type === type
      );
      const updateEntities = entityData.map((i) => {
        if (i.metadata.environment) {
          return {
            name: i.metadata.name,
            type: i.spec.type,
            lifecycle: i.spec.lifecycle,
            ...i.metadata.environment
          };
        }
        return { name: i.metadata.name, type: i.spec.type, lifecycle: i.spec.lifecycle };
      });
      setEntities((prevState) => [...prevState, ...updateEntities]);
    } else {
      const updateEntities = items.map((i) => {
        if (i.metadata.environment) {
          return {
            name: i.metadata.name,
            type: i.spec.type,
            lifecycle: i.spec.lifecycle,
            ...i.metadata.environment
          };
        }
        return { name: i.metadata.name, type: i.spec.type, lifecycle: i.spec.lifecycle };
      });
      setEntities((prevState) => [...prevState, ...updateEntities]);
    }
  }, []);
  const onSelect = useCallback(
    (_, ref, reason) => {
      if (typeof ref !== "string" && ref) {
        onChange(ref);
      } else {
        if (reason === "blur" || reason === "create-option") {
          let entityRef = ref;
          try {
            entityRef = JSON.parse(entityRef);
          } catch (err) {
          }
          if (formData !== ref) {
            onChange(JSON.parse(entityRef));
          }
        }
      }
    },
    [onChange, formData]
  );
  useEffect(() => {
    if (entities?.length === 1) {
      onChange(entities[0]);
    }
    if (!formData) {
      onChange(entities[0]);
      setEntitySelected(entities[0]);
    }
  }, [entities]);
  useEffect(() => {
    if (formData) {
      setEntitySelected(formData);
    }
  }, [onChange, formData]);
  if (loading) return /* @__PURE__ */ React.createElement(
    SkeletonComponent,
    {
      title: schema.title ?? "Reusing resources",
      description: schema.description ?? "Reuse resources from your catalog of entities"
    }
  );
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Box, { my: 1 }, /* @__PURE__ */ React.createElement(Typography, { variant: "h5" }, schema.title ?? "Reusing resources"), /* @__PURE__ */ React.createElement(Divider, null)), /* @__PURE__ */ React.createElement(Box, { className: descriptionContent }, /* @__PURE__ */ React.createElement(Typography, { variant: "body1" }, schema.description ?? "Reuse resources from your catalog of entities")), entities && entities.length > 0 ? /* @__PURE__ */ React.createElement(
    FormControl,
    {
      margin: "normal",
      required,
      error: rawErrors?.length > 0 && !formData
    },
    /* @__PURE__ */ React.createElement(
      Autocomplete,
      {
        disabled: entities?.length === 1,
        className: autocompleteWrapper,
        id: idSchema?.$id,
        value: entitySelected ? entitySelected : entities[0],
        loading,
        onChange: onSelect,
        options: entities || [],
        getOptionLabel: (option) => option.name,
        renderInput: (params) => /* @__PURE__ */ React.createElement(
          TextField,
          {
            ...params,
            label: "Select the resource...",
            variant: "outlined",
            margin: "dense",
            FormHelperTextProps: { margin: "dense", style: { marginLeft: 0 } },
            InputProps: params.InputProps
          }
        )
      }
    ),
    /* @__PURE__ */ React.createElement(FormHelperText, null, "Select the desired resource")
  ) : /* @__PURE__ */ React.createElement(Box, { className: boxInfo }, "\u26A0\uFE0F No resources available", /* @__PURE__ */ React.createElement(
    Button,
    {
      component: Link,
      to: "/create",
      style: { margin: "16px" },
      size: "large",
      variant: "outlined"
    },
    "Register Resource"
  )));
};
function resolveCatalogFilter(uiSchema) {
  const type = uiSchema["ui:options"]?.catalogFilter?.type;
  const catalogFilter = { kind: uiSchema["ui:options"]?.catalogFilter?.kind, "metadata.name": uiSchema["ui:options"]?.catalogFilter?.allowedResources };
  return [catalogFilter, type];
}

export { ResourcePicker, resolveCatalogFilter };
//# sourceMappingURL=ResourcePicker.esm.js.map
